import { useState, useCallback, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/feature/ScrollReveal';
import LogoIcon from '@/components/feature/LogoIcon';

const companyLinks = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About Us' },
  { id: 'contact', label: 'Contact Us' },
  { id: 'blog', label: 'Blogs' },
];

const serviceLinks = [
  'International Tours',
  'Domestic Tours',
  'Group Tours',
  'Family Holidays',
  'Corporate Travel',
  'Honeymoon Packages',
  'Cruise Holidays',
  'Flights & Hotels',
  'Visa Services',
];

const policyLinks = [
  { label: 'Privacy Policy', path: '/privacy-policy' },
  { label: 'Terms & Conditions', path: '/terms-and-conditions' },
  { label: 'Cancellation Policy', path: '/terms-and-conditions#cancellation' },
  { label: 'Refund Policy', path: '/terms-and-conditions#cancellation' },
  { label: 'Travel Disclaimer', path: '/terms-and-conditions#liability' },
];

interface TiltState {
  rotateX: number;
  rotateY: number;
  glowX: number;
  glowY: number;
}

function use3DTilt() {
  const [tilt, setTilt] = useState<TiltState>({ rotateX: 0, rotateY: 0, glowX: 50, glowY: 50 });

  const onMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rotateX = (y - 0.5) * -12;
    const rotateY = (x - 0.5) * 12;
    setTilt({ rotateX, rotateY, glowX: x * 100, glowY: y * 100 });
  }, []);

  const onMouseLeave = useCallback(() => {
    setTilt({ rotateX: 0, rotateY: 0, glowX: 50, glowY: 50 });
  }, []);

  return { tilt, onMouseMove, onMouseLeave };
}

function Footer3DColumn({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const { tilt, onMouseMove, onMouseLeave } = use3DTilt();

  return (
    <ScrollReveal delay={delay}>
      <div
        className={`footer-3d-col relative rounded-2xl p-4 -m-4 transition-transform duration-300 ease-out ${className}`}
        style={{
          perspective: '900px',
          transformStyle: 'preserve-3d',
          transform: `perspective(900px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) translateZ(0)`,
        }}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        {/* Glow border that tracks mouse */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{
            background: `radial-gradient(600px circle at ${tilt.glowX}% ${tilt.glowY}%, oklch(var(--primary-500) / 0.12), transparent 50%)`,
            opacity: tilt.rotateX !== 0 || tilt.rotateY !== 0 ? 1 : 0,
          }}
        />
        <div className="relative z-10">{children}</div>
      </div>
    </ScrollReveal>
  );
}

export default function Footer() {
  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
      const pathPrefix = basePath ? `/${basePath}` : '';
      window.history.pushState(null, '', `${pathPrefix}#${sectionId}`);
    }
  }, []);

  return (
    <footer className="bg-secondary-50 text-foreground-900 no-print relative overflow-hidden">
      {/* Animated top border */}
      <div className="h-[2px] w-full overflow-hidden">
        <div
          className="h-full w-[200%] animate-footerBorder"
          style={{
            background: 'linear-gradient(90deg, var(--secondary-300) 0%, var(--primary-400) 25%, var(--accent-400) 50%, var(--primary-400) 75%, var(--secondary-300) 100%)',
          }}
        />
      </div>

      <div className="w-full px-4 md:px-8 lg:px-16 pt-10 md:pt-16 pb-6 md:pb-10 3xl:max-w-[1800px] 3xl:mx-auto tv:max-w-[3000px] tv:mx-auto">
        {/* Main Grid with 3D perspective */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10"
          style={{ perspective: '1200px' }}
        >
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Footer3DColumn delay={0}>
              <div className="mb-4">
                <LogoIcon className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto" />
              </div>
              <p className="text-foreground-700 text-sm leading-relaxed max-w-sm">
                Your friendly guide to global trips. Premium travel experiences crafted with care for families, groups
                and corporate travelers worldwide.
              </p>
            </Footer3DColumn>
          </div>

          {/* Company */}
          <div>
            <Footer3DColumn delay={100}>
              <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-3 md:mb-4">Company</h4>
              <ul className="space-y-2">
                {companyLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={`#${link.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.id);
                      }}
                      className="text-xs md:text-sm text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap inline-block hover:translate-x-1 duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </Footer3DColumn>
          </div>

          {/* Services */}
          <div>
            <Footer3DColumn delay={200}>
              <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-3 md:mb-4">Services</h4>
              <ul className="space-y-2">
                {serviceLinks.map((label) => (
                  <li key={label}>
                    <a
                      href="#services"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection('services');
                      }}
                      className="text-xs md:text-sm text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap inline-block hover:translate-x-1 duration-200"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </Footer3DColumn>
          </div>

          {/* Contact & Policies */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Footer3DColumn delay={300}>
              <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-3 md:mb-4">Contact &amp; Legal</h4>
              <div className="space-y-3 mb-6">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <span className="w-5 flex-shrink-0 mt-0.5 flex justify-center">
                    <i className="ri-map-pin-line text-primary-700 text-sm md:text-base"></i>
                  </span>
                  <span className="text-sm text-foreground-700 leading-snug">
                    Lucky Plaza, Kurubatti to Hosur Main Road, Hosur, Tamil Nadu, India
                  </span>
                </div>
                {/* Phone */}
                <div className="flex items-start gap-3">
                  <span className="w-5 flex-shrink-0 mt-0.5 flex justify-center">
                    <i className="ri-phone-line text-primary-700 text-sm md:text-base"></i>
                  </span>
                  <div className="flex flex-col items-start gap-1">
                    <a href="tel:+919003560700" className="block text-sm text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer leading-snug">
                      +91 90035 60700
                    </a>
                    <a href="tel:+918884735355" className="block text-sm text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer leading-snug">
                      +91 88847 35355
                    </a>
                  </div>
                </div>
                {/* Email */}
                <div className="flex items-start gap-3">
                  <span className="w-5 flex-shrink-0 mt-0.5 flex justify-center">
                    <i className="ri-mail-line text-primary-700 text-sm md:text-base"></i>
                  </span>
                  <a href="mailto:lucky@luckyholidays.in" className="text-sm text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer leading-snug">
                    lucky@luckyholidays.in
                  </a>
                </div>
              </div>

              <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-3 md:mb-4">Policies</h4>
              <ul className="space-y-2">
                {policyLinks.map((policy) => (
                  <li key={policy.label}>
                    <Link
                      to={policy.path}
                      className="text-xs md:text-sm text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap inline-block hover:translate-x-1 duration-200"
                    >
                      {policy.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Footer3DColumn>
          </div>
        </div>

        {/* Animated Bottom Bar */}
        <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-secondary-200">
          <ScrollReveal delay={400}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-foreground-600 text-xs md:text-sm text-center sm:text-left">
                &copy; 2026 Lucky Holidays India Private Limited. All rights reserved.
              </p>
              <p className="text-foreground-500 text-xs md:text-sm">
                Made in KoolHub
              </p>
            </div>
          </ScrollReveal>

          {/* Animated gradient line under bottom bar */}
          <div className="mt-4 h-[2px] w-full rounded-full overflow-hidden relative">
            <div
              className="absolute inset-0 animate-footerBar"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, var(--primary-400) 20%, var(--accent-400) 40%, var(--primary-400) 60%, var(--accent-400) 80%, transparent 100%)',
                backgroundSize: '200% 100%',
              }}
            />
          </div>
        </div>
      </div>
    </footer>
  );
}